import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, updateDoc, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import PlayerRegistrationForm from '../auth/PlayerRegistrationForm';
import { Loader2, AlertCircle } from 'lucide-react';
import { getRegistrationFeeLabel } from '../../utils/helpers';

/**
 * Optimized RegistrationGuard Component - FIXED VERSION
 * 
 * FIX: Ensures Initial Donation is immediately visible after registration
 */
const RegistrationGuard = ({
  user,
  clubInfo,
  children,
  onRegistrationComplete,
  isAdmin = false,
  onDataReload // NEW: Callback to reload data after registration
}) => {
  const [checkingRegistration, setCheckingRegistration] = useState(true);
  const [registrationRequired, setRegistrationRequired] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);

  useEffect(() => {
    checkRegistrationStatus();
  }, [user, clubInfo]);

  const checkRegistrationStatus = async () => {
    if (!user || !clubInfo) {
      setCheckingRegistration(false);
      return;
    }

    // OPTIMIZATION 1: Check if user already has registrationCompleted flag
    if (user.registrationCompleted === true) {
      console.log('✅ Registration already completed (cached in user object)');
      setCheckingRegistration(false);
      setRegistrationRequired(false);
      return;  // Skip all checks - much faster!
    }

    // For SUY clubs, EVERYONE must fill registration form (including admins)
    // Only super-admins managing multiple clubs can skip
    const isSuperAdmin = isAdmin && user?.role === 'super-admin';

    // Check if this is a Cascadia Willow Club
    console.log('🔍 RegistrationGuard - Checking club:', clubInfo?.name);

    const isCascadiaWillowClub =
      clubInfo?.name === 'Cascadia Willow Club' ||
      clubInfo?.name === 'CWC';

    const requiresRegistration = clubInfo?.requireRegistration || isCascadiaWillowClub;

    if (!requiresRegistration) {
      // No registration needed
      setCheckingRegistration(false);
      setRegistrationRequired(false);
      return;
    }

    // Only super-admins can skip registration in Cascadia clubs
    if (isSuperAdmin) {
      console.log('✅ Super admin - skipping registration form');
      setCheckingRegistration(false);
      setRegistrationRequired(false);
      return;
    }

    // OPTIMIZATION 2: Only check Firestore if user doesn't have the flag
    // This should only happen for existing users who registered before we added the flag
    try {
      console.log('⚠️ User missing registrationCompleted flag - checking Firestore (one-time migration)');

      const regFormRef = doc(db, 'registrationForms', user.uid);
      const regFormSnap = await getDoc(regFormRef);

      if (regFormSnap.exists()) {
        // Registration exists but user doesn't have flag - update user document
        console.log('🔍 Found existing registration - updating user document with flag');

        // Update user document with the flag to avoid future checks
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          registrationCompleted: true,
          registrationCompletedAt: regFormSnap.data().submittedAt || new Date().toISOString()
        });

        setRegistrationData(regFormSnap.data());
        setRegistrationRequired(false);
        console.log('✅ User document updated with registration flag');
      } else {
        // Registration needed - user is in Cascadia club but hasn't filled form
        setRegistrationRequired(true);
        console.log('⚠️ Registration form required for Cascadia club member');
      }
    } catch (error) {
      console.error('Error checking registration status:', error);
      // For Cascadia clubs, require registration even on error
      setRegistrationRequired(true);
      console.log('⚠️ Error checking registration, requiring form for Cascadia member');
    } finally {
      setCheckingRegistration(false);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRegistrationSubmit = async (formData) => {
    if (isSubmitting) {
      console.log('⚠️ Form already submitting, ignoring duplicate click');
      return;
    }
    
    setIsSubmitting(true);
    try {
      console.log('🔍 Submitting registration form:', formData);

      // IMPORTANT: Update user profile with registrationCompleted flag
      const userRef = doc(db, 'users', user.uid);
      const updates = {
        registrationCompleted: true,  // This flag prevents future checks!
        registrationCompletedAt: new Date().toISOString(),
        phoneNumber: formData.phoneNumber || user.phoneNumber
      };

      // Update name if it was changed
      if (formData.fullName && formData.fullName !== user.displayName) {
        updates.displayName = formData.fullName;
        updates.name = formData.fullName;
        console.log('🔍 User changed name to:', formData.fullName);
      }

      await updateDoc(userRef, updates);
      console.log('✅ User profile updated with registration flag');

      // Save registration form to separate collection (for detailed data)
      const regFormRef = doc(db, 'registrationForms', user.uid);
      await setDoc(regFormRef, {
        ...formData,
        userId: user.uid,
        userEmail: user.email,
        userName: formData.fullName || user.displayName,
        clubId: clubInfo.id || clubInfo.clubId,
        clubName: clubInfo.name,
        submittedAt: new Date().toISOString(),
        status: 'completed'
      });

      // Create Initial Donation (based on format selection)
      let registrationFee = 0;
      let notesText = "Auto-created from registration form";
      
      if (formData.selectedFormats && formData.selectedFormats.length > 0) {
        // New dynamic format handling - sum the prices
        registrationFee = formData.selectedFormats.reduce((total, f) => total + (Number(f.price) || 0), 0);
        notesText += ` (${formData.selectedFormats.map(f => f.label).join(', ')})`;
      } else {
        // Legacy fallback
        const hasTennis = formData.formats?.tennis;
        const hasLeather = formData.formats?.leather;
        registrationFee = hasLeather ? 250 : (hasTennis ? 125 : 0);
        notesText += ` (${hasLeather ? 'Leather' : 'Tennis'} ball format)`;
      }

      if (registrationFee > 0) {
        console.log(`💰 Checking for existing Initial Donation for user ${user.uid}`);
        
        // IDEMPOTENCY CHECK: Ensure we don't already have an initial donation for this user in this club
        const expensesRef = collection(db, 'expenses');
        const feeQuery = query(
          expensesRef,
          where('clubId', '==', clubInfo.id || clubInfo.clubId),
          where('isInitialDonation', '==', true),
          where('splitAmong', 'array-contains', user.uid)
        );
        const existingDocs = await getDocs(feeQuery);
        
        if (!existingDocs.empty) {
          console.log('⚠️ Initial Donation already exists for this user in this club. Skipping creation.');
        } else {
          console.log(`💰 Creating Initial Donation: $${registrationFee} for user ${user.uid}`);

          const expenseData = {
            description: getRegistrationFeeLabel(clubInfo),
            amount: registrationFee,
            amountPerPlayer: registrationFee,
            gameId: null,
            gameName: null,
            tournamentId: null,
            tournamentName: null,
            clubId: clubInfo.id || clubInfo.clubId,
            splitAmong: [user.uid], // IMPORTANT: Use Firebase Auth UID
            receiptURL: null,
            createdAt: new Date().toISOString(),
            submittedBy: user.uid,
            submitterName: formData.fullName || user.displayName,
            approvalStatus: 'approved',
            approvedBy: 'system',
            approvedAt: new Date().toISOString(),
            isInitialDonation: true,
            // Legacy flag - can be removed after full migration, but keeping false for now to be explicit
            isRegistrationFee: false,
            playerEmail: user.email,
            playerName: formData.fullName || user.displayName,
            isDividable: false,
            expenseType: 'Initial Donation',
            notes: notesText
          };

          const expenseDoc = await addDoc(expensesRef, expenseData);

          console.log('✅ Initial Donation created with ID:', expenseDoc.id);
          console.log('✅ Initial Donation amount:', registrationFee);
          console.log('✅ Split among player:', user.uid);

          // FIX: Update user's balance immediately
          const currentUserDoc = await getDoc(userRef);
          const currentBalance = currentUserDoc.data()?.balance || 0;
          await updateDoc(userRef, {
            balance: currentBalance + registrationFee
          });
          console.log(`✅ Updated user balance: $${currentBalance} → $${currentBalance + registrationFee}`);
        }
      }

      console.log('✅ Registration saved successfully');

      // Update local state
      setRegistrationRequired(false);
      setRegistrationData(formData);

      // FIX: Notify parent to reload data so the new Initial Donation shows up immediately
      if (onDataReload) {
        console.log('🔄 Triggering parent data reload...');
        setTimeout(() => {
          onDataReload();
        }, 500); // Small delay to ensure Firestore has processed the write
      }

      // Notify parent component if callback provided
      if (onRegistrationComplete) {
        onRegistrationComplete(formData);
      }

      // Update the user object in memory to include the flag
      // This prevents the need to reload
      if (user && !user.registrationCompleted) {
        user.registrationCompleted = true;
        user.registrationCompletedAt = new Date().toISOString();
      }

    } catch (error) {
      console.error('❌ Error saving registration:', error);
      alert('Failed to save registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // OPTIMIZATION 3: Reduce loading time with timeout
  // If check takes too long, show the form anyway
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (checkingRegistration) {
        console.log('⚠️ Registration check timeout - showing form');
        setCheckingRegistration(false);
        setRegistrationRequired(true);
      }
    }, 2000); // 2 second timeout

    return () => clearTimeout(timeout);
  }, [checkingRegistration]);

  // Show loading while checking registration status
  if (checkingRegistration) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Checking registration status...</p>
        </div>
      </div>
    );
  }

  // Show registration form if required
  if (registrationRequired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="text-orange-500" size={28} />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Registration Required
                </h1>
                <p className="text-gray-600 mt-1">
                  Welcome to {clubInfo.name}! Please complete this one-time registration form to continue.
                </p>
              </div>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-orange-800 text-sm">
                <strong>Important:</strong> All players must complete this registration form including liability waiver
                before participating in club activities. This is a one-time requirement.
              </p>
            </div>
          </div>

          {/* Registration Form */}
          <div className="bg-white rounded-lg shadow-lg">
            <PlayerRegistrationForm
              clubName={clubInfo.name}
              clubCode={clubInfo.code || clubInfo.joinCode}
              clubLogo={clubInfo.logo}
              clubInfo={clubInfo} // Pass full club info for dynamic settings
              user={{
                uid: user.uid,
                email: user.email,
                name: user.displayName,
                displayName: user.displayName,
                phoneNumber: user.phoneNumber
              }}
              onSubmit={handleRegistrationSubmit}
              isSubmitting={isSubmitting}
              onCancel={() => {
                // Show message that registration is mandatory
                alert(
                  `Registration is mandatory for all ${clubInfo.name} players.\n\n` +
                  'This includes:\n' +
                  '• Contact information\n' +
                  '• Format preferences\n' +
                  '• Liability waiver\n\n' +
                  'You cannot access the app without completing this form.'
                );
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Registration not required or completed - render children
  return children;
};

export default RegistrationGuard;